import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { format, addDays, startOfWeek, parseISO } from "date-fns";
import { CalendarIcon, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FLAVORS = ["Vanilla", "Funfetti", "Chocolate"];
const SIZES = ['9" — 1 layer', '9" — 2 layer'];
const OCCASIONS = ["Birthday", "Wedding", "Anniversary", "Baby Shower", "Graduation", "Corporate Event", "Just Because"];
const FROSTINGS = ["Swiss Buttercream", "Cream Cheese", "Smooth Fondant", "Dark Ganache", "Whipped Cream", "Naked / Semi-Naked"];

const MIN_DATE = addDays(new Date(), 14);
const weekKey = (d) => format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

const fieldClass =
  "h-12 bg-white border-border text-primary focus:ring-1 focus:ring-accent rounded-sm data-[placeholder]:text-muted-foreground";

export default function OrderForm() {
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    flavor: "",
    size: "",
    occasion: "",
    frosting: "",
    allergies: "",
    special_instructions: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fullWeeks, setFullWeeks] = useState(new Set());
  const [weekRemaining, setWeekRemaining] = useState({});
  const [weeks, setWeeks] = useState([]);

  const loadAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/availability/weeks", { params: { count: 26 } });
      const full = new Set();
      const rem = {};
      data.forEach((w) => {
        rem[w.week_start] = w.remaining;
        if (w.full) full.add(w.week_start);
      });
      setFullWeeks(full);
      setWeekRemaining(rem);
      setWeeks(data);
    } catch {
      /* availability is best-effort; backend still enforces the limit */
    }
  }, []);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (!form.flavor || !form.size || !form.occasion || !form.frosting) {
      toast.error("Please complete all cake selections.");
      return;
    }
    if (!date) {
      toast.error("Please choose a pickup date (min. 2 weeks ahead).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/orders", {
        ...form,
        delivery_date: format(date, "yyyy-MM-dd"),
      });
      setDone(true);
      toast.success("Order request received!");
      loadAvailability();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit order.");
      if (err.response?.status === 409) {
        setDate(null);
        loadAvailability();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-border p-10 sm:p-14 rounded-sm"
        data-testid="order-success"
      >
        <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mb-6">
          <Check className="text-accent-foreground" size={26} />
        </div>
        <h3 className="font-serif text-3xl sm:text-4xl text-primary mb-4">Your request is in the oven.</h3>
        <p className="text-secondary leading-relaxed max-w-lg mb-2">
          Thank you, {form.customer_name.split(" ")[0]}. We'll review your custom cake request and email you at{" "}
          <span className="text-primary">{form.email}</span> within 48 hours to confirm details.
        </p>
        <p className="text-secondary leading-relaxed max-w-lg mb-8">
          Remember — payment is <span className="text-primary font-medium">cash on pickup</span>.
        </p>
        <Button
          data-testid="order-another-button"
          onClick={() => {
            setDone(false);
            setForm({ customer_name: "", email: "", phone: "", flavor: "", size: "", occasion: "", frosting: "", allergies: "", special_instructions: "" });
            setDate(null);
          }}
          className="btn-sweep bg-primary text-primary-foreground hover:text-primary-foreground rounded-sm h-12 px-8"
        >
          Place another request
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-border p-6 sm:p-10 rounded-sm space-y-8" data-testid="order-form">
      {/* Weekly capacity notice */}
      <div className="flex items-start gap-3 bg-muted/60 border border-border rounded-sm p-4" data-testid="capacity-notice">
        <Info size={18} className="text-accent shrink-0 mt-0.5" />
        <p className="text-sm text-secondary leading-relaxed">
          We bake just <span className="text-primary font-medium">3 cakes each week</span>, so quality never slips.
          Fully booked weeks appear greyed out on the calendar — pick any open week that suits you.
        </p>
      </div>

      {/* Contact */}
      <div className="grid sm:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-secondary">Full Name</Label>
          <Input data-testid="input-name" value={form.customer_name} onChange={(e) => set("customer_name")(e.target.value)} placeholder="Jane Baker" className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-secondary">Email</Label>
          <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="you@email.com" className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-secondary">Phone</Label>
          <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="(555) 123-4567" className={fieldClass} />
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* Cake selections */}
      <div className="grid sm:grid-cols-2 gap-6">
        <SelectField testid="select-flavor" label="Flavor" value={form.flavor} onChange={set("flavor")} options={FLAVORS} placeholder="Choose a flavor" />
        <SelectField testid="select-size" label="Size" value={form.size} onChange={set("size")} options={SIZES} placeholder="Choose a size" />
        <SelectField testid="select-occasion" label="Occasion" value={form.occasion} onChange={set("occasion")} options={OCCASIONS} placeholder="What are we celebrating?" />
        <SelectField testid="select-frosting" label="Frosting / Icing" value={form.frosting} onChange={set("frosting")} options={FROSTINGS} placeholder="Choose frosting" />
      </div>

      {/* Date picker */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-secondary">Pickup Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              data-testid="date-trigger"
              className={`${fieldClass} w-full justify-start font-normal ${!date ? "text-muted-foreground" : "text-primary"}`}
            >
              <CalendarIcon className="mr-3 h-4 w-4 text-accent" />
              {date ? format(date, "EEEE, MMMM d, yyyy") : "Select a date (min. 2 weeks out)"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border-border" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < MIN_DATE || fullWeeks.has(weekKey(d))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Allergies */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-secondary">Allergies</Label>
        <Input
          data-testid="input-allergies"
          value={form.allergies}
          onChange={(e) => set("allergies")(e.target.value)}
          placeholder="e.g. nut-free, gluten-free, dairy allergy…"
          className={fieldClass}
        />
      </div>

      {/* Special instructions */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-secondary">Special Instructions</Label>
        <Textarea
          data-testid="input-instructions"
          value={form.special_instructions}
          onChange={(e) => set("special_instructions")(e.target.value)}
          placeholder="Describe the cake you're dreaming of — colors, theme, message on top, inspiration…"
          className="min-h-[120px] bg-white border-border text-primary focus:ring-1 focus:ring-accent rounded-sm resize-none"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-secondary max-w-sm leading-relaxed">
          By submitting you acknowledge the <span className="text-primary">2-week minimum notice</span> and that payment is{" "}
          <span className="text-primary">cash on pickup</span>.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="submit-order-button"
          className="btn-sweep bg-primary text-primary-foreground hover:text-primary-foreground rounded-sm h-14 px-10 text-base tracking-wide shrink-0"
        >
          {submitting ? "Sending…" : "Request this cake"}
        </Button>
      </div>
    </form>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-secondary">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className={fieldClass}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white border-border">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="text-primary focus:bg-muted focus:text-primary">
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
